import {
  AlertTriangle,
} from "lucide-react";
import { fmtCurrency, fmtPercent, fmtSF } from "../utils/format";

// ---------------------------------------------------------------------------
// Shared utility
// ---------------------------------------------------------------------------

/** House currency formatting, but render null/zero as an em dash so the
 *  sparse parsed-statement tables stay readable. Display-only wrapper. */
function fmtDollar(val: number | undefined | null): string {
  if (val == null || val === 0) return "\u2014";
  return fmtCurrency(val);
}

// ---------------------------------------------------------------------------
// Operating Statement Parse Result
// ---------------------------------------------------------------------------

interface OpexResultProps {
  result: Record<string, unknown>;
}

export function OpexResultView({ result }: OpexResultProps) {
  const years = (result.years as Array<Record<string, unknown>>) ?? [];
  const flags = (result.flags as string[]) ?? [];
  const confidenceScore = result.confidence_score as number | undefined;
  const propertyName = result.property_name as string | undefined;

  const canonicalCategories = [
    { key: "real_estate_taxes", label: "Real Estate Taxes" },
    { key: "insurance", label: "Insurance" },
    { key: "utilities", label: "Utilities" },
    { key: "repairs_and_maintenance", label: "Repairs & Maintenance" },
    { key: "janitorial", label: "Janitorial / Cleaning" },
    { key: "landscaping", label: "Landscaping / Grounds" },
    { key: "management_fee", label: "Management Fee" },
    { key: "administrative", label: "Administrative / G&A" },
    { key: "capital_expenditures", label: "Capital Expenditures" },
    { key: "reserves", label: "Reserves" },
  ];

  return (
    <div className="pt-5 space-y-5">
      {propertyName && (
        <p className="text-sm font-medium text-bark-700">{propertyName}</p>
      )}
      {confidenceScore != null && (
        <div className="space-y-2">
          <div className="flex items-center gap-4">
            <span className="text-xs font-semibold uppercase tracking-[0.06em] text-bark-400">
              Parse Confidence
            </span>
            <div className="flex-1 max-w-xs">
              <div className="h-2.5 bg-grove-200 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all ${
                    confidenceScore >= 85 ? "bg-forest-500"
                    : confidenceScore >= 60 ? "bg-honey-500"
                    : confidenceScore >= 35 ? "bg-orange-500"
                    : "bg-red-500"
                  }`}
                  style={{ width: `${confidenceScore}%` }}
                />
              </div>
            </div>
            <span className="text-sm font-semibold text-bark-700">{confidenceScore}/100</span>
          </div>
        </div>
      )}
      {flags.length > 0 && (
        <div>
          <h4 className="text-xs font-semibold uppercase tracking-[0.06em] text-bark-400 mb-3">Flags ({flags.length})</h4>
          <div className="space-y-2">
            {flags.map((flag, i) => (
              <div key={i} className="flex items-start gap-2 px-4 py-3 rounded-lg text-xs border bg-honey-50 text-honey-800 border-honey-200">
                <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                <span>{flag}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      {years.length > 0 && (
        <div>
          <h4 className="text-xs font-semibold uppercase tracking-[0.06em] text-bark-400 mb-3">
            Expense Summary by Year ({years.length} {years.length === 1 ? "period" : "periods"})
          </h4>
          <div className="bg-white rounded-lg border border-grove-300 overflow-x-auto">
            <table className="w-full data-table text-[12px]">
              <thead>
                <tr>
                  <th className="text-left">Category</th>
                  {years.map((y, i) => (
                    <th key={i} className="num">{(y.year as number) || (y.period_label as string) || `Period ${i + 1}`}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr className="bg-forest-50 font-medium">
                  <td className="text-forest-700">Base Rent</td>
                  {years.map((y, i) => (
                    <td key={i} className="num text-forest-700">{fmtDollar(y.base_rent as number)}</td>
                  ))}
                </tr>
                <tr className="bg-forest-50/50">
                  <td className="text-forest-600">Recovery Income</td>
                  {years.map((y, i) => (
                    <td key={i} className="num text-forest-600">{fmtDollar(y.recovery_income as number)}</td>
                  ))}
                </tr>
                <tr className="bg-forest-50/50">
                  <td className="text-forest-600">Other Income</td>
                  {years.map((y, i) => (
                    <td key={i} className="num text-forest-600">{fmtDollar(y.other_income as number)}</td>
                  ))}
                </tr>
                <tr className="bg-forest-50 font-semibold border-b-2 border-grove-300">
                  <td className="text-forest-800">Total Revenue</td>
                  {years.map((y, i) => (
                    <td key={i} className="num text-forest-800">{fmtDollar(y.total_revenue as number)}</td>
                  ))}
                </tr>
                {canonicalCategories.map((cat) => {
                  const hasValues = years.some((y) => (y[cat.key] as number) !== 0);
                  if (!hasValues) return null;
                  return (
                    <tr key={cat.key}>
                      <td className="text-bark-600">{cat.label}</td>
                      {years.map((y, i) => (
                        <td key={i} className="num">{fmtDollar(y[cat.key] as number)}</td>
                      ))}
                    </tr>
                  );
                })}
                <tr className="font-semibold border-t-2 border-grove-300">
                  <td className="text-bark-800">Total Expenses</td>
                  {years.map((y, i) => (
                    <td key={i} className="num text-bark-800">{fmtDollar(y.total_expenses as number)}</td>
                  ))}
                </tr>
                <tr className="font-bold bg-honey-50">
                  <td className="text-honey-800">NOI</td>
                  {years.map((y, i) => (
                    <td key={i} className="num text-honey-800">{fmtDollar(y.noi as number)}</td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}
      {/* Uncategorized expense line items */}
      {years.length > 0 && (() => {
        const firstYear = years[0];
        const items = (firstYear.expense_items as Array<Record<string, unknown>>) ?? [];
        const unmapped = items.filter((it) => !it.is_subtotal && !it.category);
        if (unmapped.length === 0) return null;
        return (
          <div>
            <h4 className="text-xs font-semibold uppercase tracking-[0.06em] text-bark-400 mb-3">
              Uncategorized Expenses ({unmapped.length})
            </h4>
            <div className="bg-white rounded-lg border border-grove-300 overflow-x-auto">
              <table className="w-full data-table text-[12px]">
                <thead><tr><th>Line Item</th><th className="num">Amount</th></tr></thead>
                <tbody>
                  {unmapped.slice(0, 20).map((it, i) => (
                    <tr key={i}>
                      <td className="text-bark-600">{it.label as string}</td>
                      <td className="num">{fmtDollar(it.amount as number)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

// ---------------------------------------------------------------------------
// CAM Reconciliation Parse Result
// ---------------------------------------------------------------------------

interface CamRecResultProps {
  result: Record<string, unknown>;
}

export function CamRecResultView({ result }: CamRecResultProps) {
  const pools = (result.expense_pools as Array<Record<string, unknown>>) ?? [];
  const tenants = (result.tenants as Array<Record<string, unknown>>) ?? [];
  const flags = (result.flags as string[]) ?? [];
  const confidenceScore = result.confidence_score as number | undefined;
  const propertyName = result.property_name as string | undefined;
  const reconciliationYear = result.reconciliation_year as number | undefined;

  return (
    <div className="pt-5 space-y-5">
      {(propertyName || reconciliationYear) && (
        <p className="text-sm font-medium text-bark-700">
          {propertyName}{reconciliationYear ? ` \u2014 ${reconciliationYear} Reconciliation` : ""}
        </p>
      )}
      {confidenceScore != null && (
        <div className="space-y-2">
          <div className="flex items-center gap-4">
            <span className="text-xs font-semibold uppercase tracking-[0.06em] text-bark-400">
              Parse Confidence
            </span>
            <div className="flex-1 max-w-xs">
              <div className="h-2.5 bg-grove-200 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all ${
                    confidenceScore >= 85 ? "bg-forest-500"
                    : confidenceScore >= 60 ? "bg-honey-500"
                    : confidenceScore >= 35 ? "bg-orange-500"
                    : "bg-red-500"
                  }`}
                  style={{ width: `${confidenceScore}%` }}
                />
              </div>
            </div>
            <span className="text-sm font-semibold text-bark-700">{confidenceScore}/100</span>
          </div>
        </div>
      )}
      {flags.length > 0 && (
        <div>
          <h4 className="text-xs font-semibold uppercase tracking-[0.06em] text-bark-400 mb-3">Flags ({flags.length})</h4>
          <div className="space-y-2">
            {flags.map((flag, i) => (
              <div key={i} className="flex items-start gap-2 px-4 py-3 rounded-lg text-xs border bg-honey-50 text-honey-800 border-honey-200">
                <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                <span>{flag}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      {pools.length > 0 && (
        <div>
          <h4 className="text-xs font-semibold uppercase tracking-[0.06em] text-bark-400 mb-3">Expense Pools ({pools.length})</h4>
          <div className="bg-white rounded-lg border border-grove-300 overflow-x-auto">
            <table className="w-full data-table text-[12px]">
              <thead><tr><th>Category</th><th className="num">Budgeted</th><th className="num">Actual</th><th className="num">Variance</th></tr></thead>
              <tbody>
                {pools.map((p, i) => (
                  <tr key={i}>
                    <td className="font-medium text-bark-700">{p.category as string}</td>
                    <td className="num">{fmtDollar(p.budgeted_amount as number)}</td>
                    <td className="num">{fmtDollar(p.actual_amount as number)}</td>
                    <td className={`num ${((p.variance as number) || 0) < 0 ? "text-red-600" : "text-forest-600"}`}>
                      {fmtDollar(p.variance as number)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {tenants.length > 0 && (
        <div>
          <h4 className="text-xs font-semibold uppercase tracking-[0.06em] text-bark-400 mb-3">Tenant Reconciliation ({tenants.length})</h4>
          <div className="bg-white rounded-lg border border-grove-300 overflow-x-auto">
            <table className="w-full data-table text-[12px]">
              <thead><tr><th>Tenant</th><th>Suite</th><th className="num">SF</th><th className="num">Pro Rata</th><th className="num">Estimated</th><th className="num">Actual</th><th className="num">True-Up</th></tr></thead>
              <tbody>
                {tenants.slice(0, 30).map((t, i) => (
                  <tr key={i}>
                    <td className="font-medium text-bark-700">{t.tenant_name as string}</td>
                    <td className="text-bark-500">{(t.suite_id as string) || "\u2014"}</td>
                    <td className="num">{fmtSF((t.leased_sf as number) || 0)}</td>
                    <td className="num">{((t.pro_rata_share as number) || 0) > 0 ? fmtPercent(t.pro_rata_share as number, 1) : "\u2014"}</td>
                    <td className="num">{fmtDollar(t.total_estimated as number)}</td>
                    <td className="num">{fmtDollar(t.total_actual as number)}</td>
                    <td className={`num font-medium ${((t.total_true_up as number) || 0) < 0 ? "text-red-600" : "text-forest-600"}`}>
                      {fmtDollar(t.total_true_up as number)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
